import {Injectable} from "@angular/core";
import {Apollo, ApolloQueryObservable} from "apollo-angular";
import gql from "graphql-tag";
import {IAppState} from "../../shared/redux-store/store";
import {NgRedux} from "@angular-redux/store";
import {IUser} from "../../shared/redux-store/session/session.types";
import {ApolloQueryResult, MutationOptions, WatchQueryOptions} from "apollo-client";
import {environment} from "../../../environments/environment";
import {Observable} from "rxjs/Observable";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {UserQueryQuery} from "../../shared/graphql/graphql.types";
import {handleObservable} from "../../core/visbos-core/graphql/handleObservable";
import {filter, map} from "rxjs/operators";

export interface FetchUserResponse extends UserQueryQuery {
}

export const userFragments = {
  user: gql`
    fragment UserFields on User {
      cuid, label, is_editable
      name, pre_name, email, form_of_address, academic_title,
      is_deactivated customer_id
      administration_units {
        cuid, label
      }
      permissions,
      roles
    }`
};

export const updateUserPasswordMutation = gql`
  mutation UpdateUserPasswordMutation($cuid: String!, $credentials: UserCredentialsInput!) {
    updateUserCredentials(cuid: $cuid, userCredentials: $credentials) {
      ...UserFields
    }
  }
  ${userFragments.user}
`;

export const sendPasswordResetLinkQuery = gql`
  query SendPasswordResetLinkQuery($email: String!) {
    sendPasswordLink(email: $email)
  }
`;

export const FetchUsersQuery = gql`
  query UserQuery($token: String) {
    fetchUsers(token: $token) {
      ...UserFields
    }
  }

  ${userFragments['user']}
`;

export const GetRolesQuery = gql`
  query GetRoles {
    roles {
      id name label
      created_at updated_at
      permissions {
        id name
        created_at updated_at
      }
    }
  }
`;

export const GetFormsOfAddressesQuery = gql`
  query GetFormsOfAddresses {
    forms_of_addresses
  }
`;

export const GetAcademicTitlesQuery = gql`
  query GetAcademicTitles {
    academic_titles
  }
`;

export const CreateUserMutation = gql`
  mutation CreateUser($userInput:UserInput!) {
    createUser(userInput: $userInput) {
      ...UserFields
    }
  }
  ${userFragments['user']}
`;

export const UpdateUserMutation = gql`
  mutation UpdateUser($cuid: String!, $userInput:UserInput!, $userCredentials:UserCredentialsInput) {
    updateUser(cuid: $cuid, userData: $userInput, userCredentials: $userCredentials) {
      ...UserFields
    }
  }
  ${userFragments['user']}
`;

export const ResetPasswordMutation = gql`
  mutation ResetPassword($cuid: String!) {
    resetPassword(cuid: $cuid) {
      ...UserFields
    }
  }
  ${userFragments['user']}
`;

export const ReactivateUserAccountMutation = gql`
  mutation ReactivateUserAccountMutation($cuid: String, $email: String) {
    reactivateUserAccount(cuid: $cuid, email: $email) {
      ...UserFields
    }
  }
  ${userFragments.user}
`;

export interface UpdateUserCredentialsResult {
  updateUserCredentials: IUser;
}

export interface AuthenticateUserResult {
  token: string;
  authenticatedUser: IUser;
}

@Injectable()
export class UserService {

  constructor(private apollo: Apollo, private ngRedux: NgRedux<IAppState>, private http: HttpClient) {

  }

  public fetchItems<T>(options: WatchQueryOptions): ApolloQueryObservable<T> {

    let opt = Object.assign({}, options);

    if (environment.network.forceFetch) {
      opt.fetchPolicy = 'network-only';
    } else {
      opt.fetchPolicy = opt.fetchPolicy ? opt.fetchPolicy : 'cache-and-network';
    }

    let query = this.apollo.watchQuery<T>(opt).pipe(
      handleObservable(),
      filter((res: any) => (res.data != undefined))
    );

    return <ApolloQueryObservable<T>>query;
  }

  public mutate<T>(options: MutationOptions): Observable<ApolloQueryResult<T>> {

    let mutation = this.apollo.mutate(options).pipe(
      handleObservable(),
      filter((res: any) => (res.data != undefined))
    );

    return mutation;

  }

  public getUser(token: string): Observable<any> {

    // Get all stocks from Server
    let data$: any = this.fetchItems({
      query: FetchUsersQuery,
      variables: {
        token: token
      },
      fetchPolicy: "cache-and-network"
    });

    let model$ = data$
      .pipe(map(({data}: any) => {
          return {
            user: data.fetchUsers[0],
            isLoading: data.loading,
          };
        }
      ));

    return model$;
  }

  public updateUserPassword(oldPassword: string, newPassword: string, cuid?: string): Observable<ApolloQueryResult<UpdateUserCredentialsResult>> {
    return this.mutate({
      mutation: updateUserPasswordMutation,
      variables: {
        cuid: cuid || this.ngRedux.getState().session.user.cuid,
        credentials: {oldPassword, password: newPassword}
      }
    });
  }

  public sendResetPasswordLink(email: string): Observable<Object> {

    let headers = new HttpHeaders(
      {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      });
    let options = {'headers': headers};

    let body = JSON.stringify({email});
    return this.http.post(environment.passwordEMailUrl, body, options)
  }

  public resetPasswordWithToken(credentials): Observable<Object> {

    let headers = new HttpHeaders(
      {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      });
    let options = {'headers': headers};

    return this.http.post(environment.passwordResetUrl, JSON.stringify(credentials), options);
  }

  resetPasswordForUser(userCuid: string) {
    return this.mutate({
      mutation: ResetPasswordMutation,
      variables: {
        cuid: userCuid
      }
    });
  }

  public getRoles(): ApolloQueryObservable<any> {
    return this.fetchItems({
      query: GetRolesQuery
    });
  }

  public getUsers(): ApolloQueryObservable<any> {
    return this.fetchItems({
      query: FetchUsersQuery
    });
  }

  public getFormsOfAddresses(): ApolloQueryObservable<any> {
    return this.fetchItems({
      query: GetFormsOfAddressesQuery
    });
  }

  public getAcademicTitles(): ApolloQueryObservable<any> {
    return this.fetchItems({
      query: GetAcademicTitlesQuery
    });
  }

  public createUser(userInput): Observable<any> {
    return this.mutate({
      mutation: CreateUserMutation,
      variables: {
        userInput: userInput
      },
      refetchQueries: ['UserQuery']
    })
  }

  public updateUser(cuid: string, userInput, userCredentials?): Observable<any> {
    return this.mutate({
      mutation: UpdateUserMutation,
      variables: {
        cuid: cuid,
        userInput: userInput,
        userCredentials: userCredentials
      },
      refetchQueries: ['UserQuery']
    })
  }

  public deleteUser(cuid: string): Observable<any> {
    return this.mutate({
      mutation: gql`
        mutation DeleteUser($cuid: String!) {
          deleteUser(cuid: $cuid)
        }
      `,
      variables: {
        cuid: cuid
      },
      refetchQueries: ['UserQuery']
    })
  }

  public reactivateUserAccount(cuid?: string, email?: string): Observable<any> {
    return this.mutate({
      mutation: ReactivateUserAccountMutation,
      variables: {
        cuid, email
      }
    });
  }

}



// WEBPACK FOOTER //
// ./src/app/crm/user/user.service.ts